import { useContext, useMemo, useState } from 'react';
import * as Combobox from '@givebutter/primitives/combobox';
import { DateValue } from '@givebutter/primitives/date-picker';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { DateOperatorOptions } from 'components/filter/DateOperatorOptions';
import { FilterComboboxItems } from 'components/filter/filters-next/FilterComboboxItems';
import { FilterContainer } from 'components/filter/filters-next/FilterContainer';
import { FilterContext } from 'components/filter/filters-next/FilterContext';
import { FilterForm } from 'components/filter/filters-next/FilterForm';
import { FilterFormContext } from 'components/filter/filters-next/FilterFormContext';
import { FilterSubmit } from 'components/filter/filters-next/FilterSubmit';
import { ReadableFilterValue } from 'components/filter/filters-next/ReadableFilterValue';
import { operatorClassName } from 'components/filter/filters-next/props';
import {
    transformDateOperatorToReadable,
    transformDateToReadable,
} from 'components/filter/filters-next/readable';
import { useCurrentFilter } from 'components/filter/filters-next/useCurrentFilter';
import { useFilter } from 'components/filter/filters-next/useFilter';
import {
    transformInboundDate,
    transformNullableDateToRequest,
} from 'components/filter/filters-next/util';
import {
    ContentFilterComponent,
    DownstreamFilterTransform,
    FilterFormValue,
    FilterIcon,
    FilterOperator,
    LabelFilterComponent,
    ReadableFilterComponent,
    ServerKey,
    UpstreamFilterTransform,
} from 'components/filter/types';
import * as Form from 'components/form';
import { capitalize } from 'lodash';
import { DateTime } from 'luxon';

type DueAtPreset =
    | 'overdue'
    | 'today'
    | 'tomorrow'
    | 'this_week'
    | 'next_week'
    | 'this_month'
    | 'next_month'
    | 'no_due_date'
    | 'custom';

interface DueAtValue {
    preset: DueAtPreset[];
    value: DateValue | null;
}

interface DueAtFilterFormValues extends FilterFormValue<DueAtValue> {}

const setValueOptions = {
    shouldDirty: true,
    shouldTouch: true,
};

const presets: DueAtPreset[] = [
    'overdue',
    'today',
    'tomorrow',
    'this_week',
    'next_week',
    'this_month',
    'next_month',
    'no_due_date',
    'custom',
] as const;

export const filterKey: ServerKey = 'due_at';
export const Label: LabelFilterComponent = () => 'Due';

export const Icon: FilterIcon = faCalendar;

export const Filter: ContentFilterComponent = () => {
    const [showCustom, setShowCustom] = useState(false);
    const { defaultValue, ...rest } = useFilter<DueAtValue>();
    const { setIsOpen, strategyType: strategyTypeRaw } = useContext(FilterContext);
    const strategyType = strategyTypeRaw as ServerKey;
    const methods = Form.useForm<DueAtFilterFormValues>({
        defaultValues: {
            value: defaultValue ? defaultValue.value : { preset: [], value: null },
            filterOperator: defaultValue?.filterOperator ? defaultValue.filterOperator : 'eq',
        },
        mode: 'onChange',
    });

    const { watch, path, control, setValue } = methods;
    const filterOperator = watch('filterOperator');

    const ctx = useMemo(
        () => ({
            defaultValue,
            ...rest,
        }),
        [defaultValue, rest],
    );

    const handleSelectItem: Combobox.ComboboxItemProps['onSelectItem'] = ({ selectedItem }) => {
        if (!selectedItem) return;

        const id = selectedItem as DueAtPreset;
        const operator = getOperatorFromPreset(id);
        const value = transformDueAtPresetToValue(id);

        setValue('value.preset', [id], setValueOptions);
        setValue('value.value', value, setValueOptions);
        setValue('filterOperator', operator, setValueOptions);

        if (selectedItem !== 'custom') {
            setIsOpen(false);
        } else {
            setShowCustom(true);
        }
    };

    return (
        <FilterFormContext.Provider value={ctx}>
            <Form.Provider {...methods}>
                <FilterForm>
                    <FilterContainer>
                        {showCustom ? (
                            <Form.Container className="gap-3">
                                <div className="flex items-center gap-1">
                                    <Form.Label name={path('value')}>
                                        <Label strategyType={strategyType} />
                                    </Form.Label>
                                    <Form.SingleSelect control={control} name="filterOperator">
                                        <SingleSelect.Trigger
                                            aria-label="Filter operator"
                                            className={operatorClassName}
                                            size="sm"
                                        />
                                        <SingleSelect.Content>
                                            <DateOperatorOptions />
                                        </SingleSelect.Content>
                                    </Form.SingleSelect>
                                    <Form.Label className="sr-only" name={path('filterOperator')}>
                                        <Label strategyType={strategyType} />
                                    </Form.Label>
                                </div>
                                <Form.Container className="[&>*]:items-center [&_.react-calendar]:w-72 [&_.react-calendar]:p-0">
                                    <Form.UseFormDatePicker
                                        name="value.value"
                                        control={control}
                                        selectRange={filterOperator === 'bet'}
                                        isTextInput={false}
                                    />
                                    <Form.Error name={path('value')} />
                                </Form.Container>
                            </Form.Container>
                        ) : (
                            <Form.Combobox
                                control={control}
                                name="value.preset"
                                allowMultiple={false}
                            >
                                <FilterComboboxItems>
                                    {presets.map(preset => (
                                        <Combobox.Item
                                            key={preset}
                                            value={preset}
                                            onSelectItem={handleSelectItem}
                                        >
                                            <Combobox.ItemText>
                                                {getPresetLabel(preset)}
                                            </Combobox.ItemText>
                                            <Combobox.CheckmarkIndicator />
                                        </Combobox.Item>
                                    ))}
                                </FilterComboboxItems>
                            </Form.Combobox>
                        )}
                        <FilterSubmit />
                    </FilterContainer>
                </FilterForm>
            </Form.Provider>
        </FilterFormContext.Provider>
    );
};
export const Readable: ReadableFilterComponent = () => {
    const currentFilter = useCurrentFilter<DueAtValue>();
    const operator = currentFilter?.filterOperator;
    const value = currentFilter?.value;

    if (!value) return null;
    return (
        <ReadableFilterValue>
            {value.preset.includes('custom') ? (
                <>
                    {operator ? transformDateOperatorToReadable(operator) : ''}{' '}
                    {transformDateToReadable(value.value ?? [])}
                </>
            ) : (
                getPresetLabel(value.preset.at(0) ?? null)
            )}
        </ReadableFilterValue>
    );
};

export const downstream: DownstreamFilterTransform<string> = v => transformInboundDate(v);

export const upstream: UpstreamFilterTransform<DueAtValue> = filter =>
    transformNullableDateToRequest(filter.value.value, 'and', filter.filterOperator);

const getPresetLabel = (preset: DueAtPreset | null) => capitalize(preset?.split('_').join(' '));

const transformDueAtPresetToValue = (preset: DueAtPreset): DateValue | null => {
    switch (preset) {
        case 'today':
            return DateTime.now();
        case 'tomorrow':
            return DateTime.now().plus({ days: 1 });
        case 'overdue':
            return DateTime.now().minus({ days: 1 });
        case 'this_week':
            return [DateTime.now().startOf('week'), DateTime.now().endOf('week')];
        case 'next_week':
            return [
                DateTime.now().plus({ weeks: 1 }).startOf('week'),
                DateTime.now().plus({ weeks: 1 }).endOf('week'),
            ];
        case 'this_month':
            return [DateTime.now().startOf('month'), DateTime.now().endOf('month')];
        case 'next_month':
            return [
                DateTime.now().plus({ months: 1 }).startOf('month'),
                DateTime.now().plus({ months: 1 }).endOf('month'),
            ];
        case 'custom':
            return DateTime.now();
        case 'no_due_date':
        default:
            return null;
    }
};

const getOperatorFromPreset = (preset: DueAtPreset): FilterOperator => {
    switch (preset) {
        case 'today':
        case 'tomorrow':
        case 'no_due_date':
        case 'custom':
            return 'eq';
        case 'this_week':
        case 'next_week':
        case 'this_month':
        case 'next_month':
            return 'bet';
        case 'overdue':
            return 'lt';
    }
};

export type { DueAtValue, DueAtPreset };
